import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React from 'react';
import { Separator, Tooltip, TooltipToggle } from 'telia-front-react';
import { CALLS_PACKAGE_ID } from '../stores/MobileLifeStore';
import { useStores } from '../stores/RootStore';
import { formatPrice, getPrivateMobileLifeBasePrice, mobileLifePricePerUser, mobileLifeTotalPrice } from '../util/PriceUtil';
import { PrivateMobileCardProps } from './PrivateMobileCard';

type PrivateMobileCardPriceProps = Pick<
  PrivateMobileCardProps,
  | 'packageIds'
  | 'callsPackagePrice'
  | 'forSingleUserOnly'
  | 'multiUserDistributedPrice'
  | 'multiUserDistributedCampaignPrice'
  | 'singleUserDistributedPrice'
  | 'singleUserDistributedCampaignPrice'
  | 'hideViewDetailsButton'
  | 'packageCustomDistributedPrices'
  | 'packageCustomDistributedCampaignPrices'
  | 'campaignRulesLink'
>;

const PrivateMobileCardPrice = observer((props: PrivateMobileCardPriceProps) => {
  const {
    forSingleUserOnly,
    multiUserDistributedPrice,
    multiUserDistributedCampaignPrice,
    singleUserDistributedPrice,
    singleUserDistributedCampaignPrice,
    packageCustomDistributedPrices,
    packageCustomDistributedCampaignPrices,
    campaignRulesLink,
    hideViewDetailsButton
  } = props;
  const { mobileLifeStore } = useStores();
  const { translate } = React.useContext(TranslationsContext);
  const packageId = mobileLifeStore.getPackageId(props.packageIds);
  const internetPrices = mobileLifeStore.getPackagePrices(packageId);

  const hasPackageDiscount = (): boolean => {
    const hasInternetDiscount = mobileLifeStore.hasPackageDiscount(packageId);
    const hasCallsDiscount = mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID);
    return hasInternetDiscount || hasCallsDiscount;
  };

  const getCallsPrice = (): number => {
    if (mobileLifeStore.hasPackageDiscount(CALLS_PACKAGE_ID)) {
      return mobileLifeStore.getPackagePrices(CALLS_PACKAGE_ID).price;
    }
    return props.callsPackagePrice;
  };

  const callsPrice = getCallsPrice();
  const hasDiscount = hasPackageDiscount();
  const hasCampaign = !hasDiscount && !!multiUserDistributedCampaignPrice;

  const basePrice = getPrivateMobileLifeBasePrice(
    mobileLifeStore.selectedDeviceCount,
    internetPrices?.price,
    multiUserDistributedPrice,
    singleUserDistributedPrice,
    packageCustomDistributedPrices,
    hasDiscount
  );

  const campaignPrice = getPrivateMobileLifeBasePrice(
    mobileLifeStore.selectedDeviceCount,
    internetPrices?.price,
    multiUserDistributedCampaignPrice ?? multiUserDistributedPrice,
    singleUserDistributedCampaignPrice ?? singleUserDistributedPrice,
    packageCustomDistributedCampaignPrices ?? packageCustomDistributedPrices,
    hasDiscount
  );

  const totalPrice = mobileLifeTotalPrice(campaignPrice, callsPrice, forSingleUserOnly ? 1 : mobileLifeStore.selectedDeviceCount);

  const renderDiscountedRegularPrice = (): JSX.Element => {
    if (!hasDiscount && !hasCampaign) {
      return null;
    }
    const normalPrice = mobileLifeTotalPrice(
      +(internetPrices?.priceWithoutDiscount ?? basePrice),
      +callsPrice,
      forSingleUserOnly ? 1 : mobileLifeStore.selectedDeviceCount
    );

    return (
      <>
        <p className="text-small">{translate('mobile.package.regular.price', { price: formatPrice(normalPrice) })}</p>
        <Separator spacing="xxsmall" color="invisible" />
      </>
    );
  };

  return (
    <>
      {mobileLifeStore.selectedDeviceCount > 1 && !forSingleUserOnly ? (
        <p>
          {formatPrice(mobileLifePricePerUser(totalPrice, mobileLifeStore.selectedDeviceCount))} {translate('mobile.package.user')}
        </p>
      ) : (
        !hideViewDetailsButton && <Separator spacing="xsmall" color="invisible" />
      )}
      {renderDiscountedRegularPrice()}
      {hasCampaign || hasDiscount ? (
        <Tooltip
          referenceElement={
            <TooltipToggle id={`private-mobile-card-price-tooltip-${packageId}`}>
              <span className="text-bold h4 text__margin-none text-campaign">
                {translate(`mobile.package.${hasDiscount ? 'discount' : 'campaign'}.eurPerMonth`, { price: formatPrice(totalPrice) })}
              </span>
            </TooltipToggle>
          }
        >
          {translate(`mobile.package.${hasDiscount ? 'discount' : 'campaign'}.tooltip`, { price: formatPrice(totalPrice) })}
        </Tooltip>
      ) : (
        <p className="h4 text__margin-none">
          {formatPrice(totalPrice)} {translate('mobile.eurPerMonth')}
        </p>
      )}
      {hasCampaign && campaignRulesLink}
    </>
  );
});

export default PrivateMobileCardPrice;
